import {
  Tables,
  TablesInsert,
  TablesUpdate,
} from '@/lib/supabase/supabase_types_override';
import { supabase } from '@/lib/supabase/supabaseClient';
import { createCrm } from '../useCreateCrm';
import {
  addSurveyWithQuestions,
  AddSurveyWithQuestionsProps,
} from '@/features/survey/hooks/useMutation/useAddSurveyWithQuestions';
import { getAirtableMetaDataFromConfiguration } from '@/lib/utils/crm/airtable/getAirtableMetaDataFromConfiguration';
import { matchConfigurationDataWithDefault } from '@/lib/utils/crm/matchConfigurationData';
import { getTable } from '@/lib/utils/crm/airtable/tables';
import { crmRegistry } from '@/features/crm_system/lib/_core/crmRegistry';
import { normalizeAirtableFields } from '@/features/crm_system/lib/crm/airtable/__utils/normalizeAirtableFieldTypesIntoQuestions';

export type CreateCrmWithProjectsData = {
  crm: TablesInsert<'crm'>;
  updateProjectsIds?: string[];
  crm_project?: (TablesInsert<'crm_project'> & {
    crm_config?: Omit<TablesInsert<'crm_config'>, 'crm_id'>[] | null;
  })[];
  createProjectsAutomatically?: boolean;
  // createSurveysAutomatically?: boolean
  // createQuestionsAutomatically?: boolean
};

export type CreateCrmWithProjectsResult = {
  crm: Tables<'crm'>;
  crm_project: Array<
    Tables<'crm_project'> & { crm_config: Tables<'crm_config'>[] }
  >;
  project: Array<
    Tables<'project'> & {
      survey: Array<
        Tables<'survey'> & {
          question: Array<Tables<'question'>>;
        }
      >;
    }
  >;
};

export const createCrmWithProjects = async ({
  crm: crmData,
  updateProjectsIds: projectIds,
  crm_project: crmProjects,
  createProjectsAutomatically,
}: CreateCrmWithProjectsData): Promise<CreateCrmWithProjectsResult> => {
  const crmResult = await createCrm(crmData);
  console.log(crmData, projectIds, crmProjects, createProjectsAutomatically)
  if (!crmResult) throw new Error('Failed to create CRM');

  const crmSystemConfig = crmRegistry.getSystemById(crmResult.crm_system);
  const createdCrmProjects: CreateCrmWithProjectsResult['crm_project'] = [];

  if (crmProjects && crmProjects.length > 0) {
    for (const crmProject of crmProjects) {
      const { crm_config: crmConfigs, ...crmProjectData } = crmProject;
      const crmProjectResult = await supabase
        .from('crm_project')
        .insert({
          ...crmProjectData,
          crm_id: crmResult.id,
        })
        .throwOnError()
        .select()
        .maybeSingle()
        .then((res) => res.data);
      
      const createdCrmConfigs: Tables<'crm_config'>[] = [];
      for (const crmConfig of crmConfigs || []) {
        const crmConfigResult = await supabase
          .from('crm_config')
          .insert({
            ...crmConfig,
            crm_id: crmResult.id,
            crm_project_id: crmProjectResult?.id,
          })
          .throwOnError()
          .select()
          .maybeSingle()
          .then((res) => res.data);
        if (crmConfigResult) createdCrmConfigs.push(crmConfigResult);
      }

      if (crmProjectResult) {
        createdCrmProjects.push({
          ...crmProjectResult,
          crm_config: createdCrmConfigs,
        });
      }
    }
  }

  if (projectIds && projectIds.length > 0) {
    const projectUpdates: TablesUpdate<'project'>[] = projectIds.map(
      (projectId) => ({
        id: projectId,
        crm_id: crmResult.id,
        crm_system: crmResult.crm_system,
      })
    );

    for (const project of projectUpdates) {
      await supabase.from('project').update(project).throwOnError();
    }
  }
  const createdProjects: CreateCrmWithProjectsResult['project'] = [];
  if (createProjectsAutomatically) {
    await Promise.all(
      createdCrmProjects.map(async (crmProject) => {
        const projectRes = await supabase
          .from('project')
          .insert({
            name: `${crmProject.name} - Project`,
            organisation_id: Number(crmResult?.organisation_id),
            crm_project_id: crmProject.id,
            crm_system: crmResult.crm_system,
            crm_id: crmResult.id
          })
          .throwOnError()
          .select()
          .maybeSingle()
          .then((res) => res.data);
        const createdProject: CreateCrmWithProjectsResult['project'][number] = {
          ...projectRes,
          survey: [],
        };
        if (!projectRes) throw new Error('Error creating project');
        await Promise.all(
          crmProject.crm_config.map(async (crmConfig) => {
            const fullConfiguration = matchConfigurationDataWithDefault(
              crmResult,
              crmProject,
              crmConfig
            );
            let questions: AddSurveyWithQuestionsProps['questions'] = [];
            if (!fullConfiguration)
              throw new Error("Can't obtain configuration data");
            if (crmResult.crm_system === 'airtable') {
              const { apiKey, baseId, tableNameOrId } =
                getAirtableMetaDataFromConfiguration(fullConfiguration);
              if (!apiKey || !baseId || !tableNameOrId)
                throw new Error(
                  "Api Key, Base Id or Table Id is missing, can't obtain table data to create questions"
                );
              const airtableTable = await getTable({
                apiKey,
                baseId,
                tableNameOrId,
              });
              if (airtableTable) {
                questions = airtableTable.fields
                  .filter(
                    (field) =>
                      !crmSystemConfig?.notSupportedFieldTypes?.includes(
                        field.type
                      )
                  )
                  .map((field) => normalizeAirtableFields(field, {survey_id: 'temp'}));
              }
            }
            const {survey: createdSurvey, questions: createdQuestions} = await addSurveyWithQuestions({
              crm_system: crmResult.crm_system,
            })({
              name: `${crmConfig.name} - Survey`,
              project_id: projectRes.id,
              crm_config_id: crmConfig.id,
              crm_id: crmResult.id,
              questions,
              organisation_id: crmData.organisation_id,
            });
            createdProject.survey.push({...createdSurvey, question: createdQuestions})
          })
        );
        createdProjects.push(createdProject)
      })
    );
  }
  return { crm: crmResult, crm_project: createdCrmProjects, project: createdProjects };
};
